import { useTranslation } from "@resamare/translation";
import React, { createContext, useContext, useMemo } from "react";
import { useClientConfig } from "../client-details";

interface CurrencyContextType {
    formatCurrency: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({
    children,
    defaultLanguage = "EUR",
}: {
    children: React.ReactNode;
    defaultLanguage?: "EUR" | "XPF";
}) {
    const { currentLanguage } = useTranslation();
    const config = useClientConfig();
    const currency = config.devise || defaultLanguage;

    const formatCurrency = useMemo(
        () =>
            new Intl.NumberFormat(currentLanguage, {
                style: "currency",
                currency: config.devise || "EUR",
                minimumFractionDigits: currency === "XPF" ? 0 : 2,
                maximumFractionDigits: currency === "XPF" ? 0 : 2,
            }).format,
        [currentLanguage, currency],
    );

    const contextValue = useMemo(
        () => ({
            formatCurrency,
        }),
        [currency, currentLanguage],
    );

    return <CurrencyContext.Provider value={contextValue}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error("useCurrency must be used within a CurrencyProvider");
    }
    return context;
}
